import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

/**
 * Generic chart toolbar dropdown shell.
 *
 * Renders the labelled toggle button + the popover panel with the
 * usual dark surface, border, shadow, scroll behaviour, and
 * click-outside-to-close handling. The actual contents of the panel
 * are passed in via children so each chart toolbar dropdown
 * (Indicators / Cycles / My Data / etc.) can render its own item
 * list, sub-sections, and clear-all buttons without re-implementing
 * the dropdown chrome.
 *
 * Behaviour notes:
 *  - Click outside the wrapper closes the dropdown via onToggle(false).
 *  - The "active" pill in the button label is shown when activeCount
 *    is a finite positive number; otherwise the count is hidden.
 *  - Each instance owns its own ref so multiple dropdowns can coexist
 *    on the same page without colliding.
 */
export default function ChartToolbarDropdown({
  label,
  activeCount,
  isOpen,
  onToggle,
  minWidth = 220,
  children,
}) {
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        onToggle(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onToggle]);

  const showCount = Number.isFinite(activeCount) && activeCount > 0;

  return (
    <div style={{ position: 'relative' }} ref={wrapperRef}>
      <button
        onClick={() => onToggle(!isOpen)}
        style={{
          backgroundColor: 'var(--dwlf-ink-20)',
          color: 'var(--dwlf-text)',
          border: 'none',
          padding: '5px 8px',
          fontSize: 12,
          cursor: 'pointer',
          borderRadius: 4,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <span>{label}</span>
        {showCount && (
          <span
            style={{
              color: 'var(--dwlf-ink-55)',
              fontSize: 11,
              fontWeight: 500,
            }}
          >
            {activeCount}
          </span>
        )}
        <span style={{ fontSize: 10, opacity: 0.7 }}>▼</span>
      </button>
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: 2,
            backgroundColor: 'var(--dwlf-elevated)',
            border: '1px solid var(--dwlf-ink-30)',
            borderRadius: 4,
            minWidth,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            zIndex: 1000,
            maxHeight: '70vh',
            overflowY: 'auto',
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

ChartToolbarDropdown.propTypes = {
  label: PropTypes.string.isRequired,
  activeCount: PropTypes.number,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  minWidth: PropTypes.number,
  children: PropTypes.node,
};
